import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import MySchedule from "@/components/leagues/MySchedule";
import StandingsTable from "@/components/leagues/StandingsTable";
import LeagueInfoCard from "@/components/leagues/LeagueInfoCard";
import LeagueRoundsView from "@/components/leagues/LeagueRoundsView";
// UPPDRAG-42: render kategori-badges från text[]-arrayen istället för
// en enda fritext-badge. Helpern hanterar legacy-fall (tom array →
// derive från fritext-fältet).
import { resolveLeagueCategories, LEAGUE_CATEGORY_LABELS } from "@/lib/leagueCategories";
// TASK-66 PR-D: hook centralizes the union of teams.player1/2 +
// team_members.user_id (was UPPDRAG-62 PR-D's three-source union;
// captain_id stripped — captain users still resolved via team_members).
import { useMyTeamForLeague } from "@/hooks/useMyTeamForLeague";

const statusLabels: Record<string, string> = {
  draft: "Utkast", registration_open: "Anmälan öppen",
  active: "Pågår", completed: "Avslutad", archived: "Arkiverad",
};


export default function LeagueDetail() {
  const { id } = useParams<{ id: string }>();
  const [showRegistration, setShowRegistration] = useState(false);

  const { data: league, isLoading } = useQuery({
    queryKey: ["league", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leagues")
        .select("*, halls(name, address)")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // TASK-66 PR-D: useMyTeamForLeague returns union of teams.player1/2 ∪
  // team_members.user_id, deduped. Captain users still resolve via the
  // team_members branch.
  const { team: myTeam } = useMyTeamForLeague(id);

  const { data: teamCount } = useQuery({
    queryKey: ["league-team-count", id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("teams")
        .select("*", { count: "exact", head: true })
        .eq("league_id", id!)
        .eq("status", "active");
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="space-y-4 pb-20">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-60 w-full rounded-lg" />
      </div>
    );
  }

  if (!league) {
    return (
      <div className="py-20 text-center text-muted-foreground">
        <p>Serien hittades inte</p>
        <Button variant="link" asChild><Link to="/leagues">← Tillbaka</Link></Button>
      </div>
    );
  }

  const isOpen = league.status === "registration_open";
  const alreadyRegistered = !!myTeam;

  return (
    <div className="space-y-4 pb-20">
      <Link to="/leagues" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Tillbaka
      </Link>

      {/* Header */}
      {/* UPPDRAG-42: rendera en badge per kategori istället för en samlad
          fritext-badge. Faller tillbaka till legacy `category` när
          `categories` saknas eller är tom (samma regel som migrationens
          backfill). */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-bold">{league.name}</h1>
          {(() => {
            const cats = resolveLeagueCategories(league as { categories?: string[] | null; category?: string | null });
            if (cats.length === 0) {
              // Legacy fallback: render the raw text if helper couldn't
              // map it (e.g. "Junior", "Företag" from older free-form values).
              return <Badge variant="secondary">{league.category}</Badge>;
            }
            return cats.map((c) => (
              <Badge key={c} variant="secondary">{LEAGUE_CATEGORY_LABELS[c]}</Badge>
            ));
          })()}
        </div>
        <Badge variant={isOpen ? "default" : "outline"}>
          {statusLabels[league.status] || league.status}
        </Badge>
      </div>

      {/* Tabs if registered, otherwise just info */}
      {alreadyRegistered && myTeam?.division_id ? (
        <Tabs defaultValue="info">
          <TabsList className="w-full">
            <TabsTrigger value="info" className="flex-1">Info</TabsTrigger>
            <TabsTrigger value="schedule" className="flex-1">Schema</TabsTrigger>
            {/* PR-10.2: player-facing rounds view (admin defines them in
                LeagueForm's Omgångar tab from PR-10.1; this is the
                read-only player counterpart). */}
            <TabsTrigger value="rounds" className="flex-1">Omgångar</TabsTrigger>
            <TabsTrigger value="standings" className="flex-1">Tabell</TabsTrigger>
          </TabsList>
          <TabsContent value="info">
            <LeagueInfoCard
              league={league}
              teamCount={teamCount}
              myTeam={myTeam}
              isOpen={isOpen}
              alreadyRegistered={alreadyRegistered}
              showRegistration={showRegistration}
              setShowRegistration={setShowRegistration}
            />
          </TabsContent>
          <TabsContent value="schedule">
            <MySchedule leagueId={league.id} teamId={myTeam.id} league={league} />
          </TabsContent>
          <TabsContent value="rounds">
            <LeagueRoundsView
              leagueId={league.id}
              roundsVisibleAhead={league.rounds_visible_ahead}
            />
          </TabsContent>
          <TabsContent value="standings">
            <StandingsTable
              divisionId={myTeam.division_id}
              teamId={myTeam.id}
              promotionCount={league.promotion_count}
              relegationCount={league.relegation_count}
            />
          </TabsContent>
        </Tabs>
      ) : (
        <LeagueInfoCard
          league={league}
          teamCount={teamCount}
          myTeam={myTeam}
          isOpen={isOpen}
          alreadyRegistered={alreadyRegistered}
          showRegistration={showRegistration}
          setShowRegistration={setShowRegistration}
        />
      )}
    </div>
  );
}
